import { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useDispatch } from 'react-redux'

// Components
import Track from 'components/track'
import { Image } from '@tracklib/kit'

// Actions
import { actions as browseActions } from 'modules/browse/actions'

// Hooks
import { useRequest } from 'modules/requests/hooks'
import { useAudioPlayStates, useAudio } from 'modules/audio/hooks'
import { useIsInFavoritesCollection } from 'modules/collections/hooks'

// Utils
import { createSongItemId } from 'utils'
import { DEFAULT_EASE } from '@constants'

import styles from './interactiveguide.css'

type InteractiveGuideProps = {
  page: number
  paginate: () => void
}

export const trackId = 98113

export const Video = ({ className, mp4, webm, cover }: { className?: string; mp4: string; webm?: string; cover?: number | string }) => (
  <video autoPlay muted loop playsInline className={className}>
    {webm && <source src={webm} type="video/webm" />}
    <source src={mp4} type="video/mp4" />
    {cover && <Image image={cover} absolute lazy alt="" />}
  </video>
)

const InteractiveGuide = ({ page, paginate }: InteractiveGuideProps): JSX.Element => {
  const dispatch = useDispatch()
  const query = `ids=${trackId}`
  useEffect(() => {
    dispatch(browseActions.fetchTracksBySearchQuery(query))
  }, []) // eslint-disable-line
  const request = useRequest('FETCH_TRACKS_BY_SEARCH_QUERY', query)

  const getAnimate = (isList?: boolean) => {
    if (page === 0) {
      return { scale: isList ? 1.1 : 2, x: 0, opacity: isList ? 0 : 1 }
    } else if (page === 1 || page === 2) {
      return {
        scale: 1.1,
        x: 0,
        opacity: 1,
        transition: {
          scale: {
            type: 'spring',
            duration: 0.7,
            bounce: 0.2,
          },
          opacity: { duration: 0.7, ease: DEFAULT_EASE },
        },
      }
    } else {
      return {
        scale: 1,
        x: '-50%',
        opacity: isList ? 0 : 1,
        transition: { duration: 0.5, ease: DEFAULT_EASE },
      }
    }
  }

  const covers = [
    '/static/images/onboard/cover1.jpg',
    '',
    '',
    '/static/images/onboard/cover2.jpg',
    '/static/images/onboard/cover3.jpg',
    '/static/images/onboard/cover4.png',
  ]
  const tracks = ['/static/images/onboard/track1.png', '', '/static/images/onboard/track2.png']

  const contextKey = 'onboarding'
  const songItemId = createSongItemId(trackId)
  const { isPlaying, isLooping } = useAudioPlayStates(songItemId, contextKey)
  const { loop } = useAudio()
  const isInFavorites = useIsInFavoritesCollection(+trackId, loop)

  useEffect(() => {
    if (isPlaying && page === 0) {
      paginate()
    }
  }, [isPlaying, page]) // eslint-disable-line

  useEffect(() => {
    if (isLooping && page === 1) {
      paginate()
    }
  }, [isLooping, page]) // eslint-disable-line

  useEffect(() => {
    if (isInFavorites && page === 3) {
      paginate()
    }
  }, [isInFavorites, page]) // eslint-disable-line

  if (page === 4) {
    return <Video className={styles.video} mp4="/static/images/onboard/desktop-onboarding-browse-catalog.mp4" />
  }

  return (
    <div>
      <div className={styles[`blocker${page}`]} />
      <motion.div
        initial={{ scale: 1 }}
        animate={page === 3 ? { scale: 1.4 } : { scale: 1 }}
        transition={{ duration: 0.5, ease: DEFAULT_EASE }}
      >
        <AnimatePresence>
          {page === 0 && (
            <motion.ul
              key="page1"
              className={styles.coverGrid}
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, scale: 2 }}
            >
              {covers.map(cover => (
                <li key={cover !== '' ? cover : Math.random()} style={{ backgroundImage: `url("${cover}")` }} />
              ))}
            </motion.ul>
          )}
          <motion.ol
            key="page2"
            className={styles.fakeList}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={getAnimate(true)}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            {tracks.map(track => (
              <li key={track} style={{ backgroundImage: `url("${track}")` }} />
            ))}
          </motion.ol>
        </AnimatePresence>

        <motion.div className={styles.realTrackWrapper} initial={false} animate={getAnimate()}>
          {request.isDone && (
            <Track
              trackId={trackId}
              songItemId={songItemId}
              mainClassName={page === 0 ? styles.realTrackMain : ''}
              forceHover
              contextKey={contextKey}
              animatePlayButton
              animateLoopButton={page === 1}
              coverSizes="280px"
            />
          )}
        </motion.div>
      </motion.div>
    </div>
  )
}

export default InteractiveGuide
